import { useState, useMemo } from 'react'
import { generateRandomList } from './utils/generator'
import { DEFAULT_CONFIG } from './constants'
import './App.css'

function App() {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [results, setResults] = useState(() =>
    generateRandomList(DEFAULT_CONFIG)
  )
  const [error, setError] = useState('')
  const [copiedIndex, setCopiedIndex] = useState(null)
  const [copyError, setCopyError] = useState('')

  const validLength = useMemo(() => {
    const parsed = Number(config.length)
    if (Number.isNaN(parsed)) return DEFAULT_CONFIG.length
    return Math.min(32, Math.max(4, Math.floor(parsed)))
  }, [config.length])

  const handleLengthChange = (event) => {
    const next = event.target.value
    setConfig((prev) => ({ ...prev, length: next }))
  }

  const handleToggle = (key) => (event) => {
    const { checked } = event.target
    setConfig((prev) => ({ ...prev, [key]: checked }))
  }

  const handleGenerate = () => {
    try {
      const normalizedConfig = {
        length: validLength,
        includeLetters: config.includeLetters,
        includeDigits: config.includeDigits,
        includeSymbols: config.includeSymbols,
      }
      const nextResults = generateRandomList(normalizedConfig)
      setResults(nextResults)
      setError('')
      setConfig((prev) => ({ ...prev, length: normalizedConfig.length }))
      setCopiedIndex(null)
      setCopyError('')
    } catch (err) {
      setError(err.message)
    }
  }

  const copyWithFallback = (value) => {
    const textarea = document.createElement('textarea')
    textarea.value = value
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'absolute'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }

  const handleCopy = async (value, index) => {
    try {
      if (copyError) {
        setCopyError('')
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else {
        copyWithFallback(value)
      }
      setCopiedIndex(index)
      window.setTimeout(() => {
        setCopiedIndex((current) => (current === index ? null : current))
      }, 2000)
    } catch (err) {
      setCopyError('复制失败，请手动复制。')
    }
  }

  const disabled =
    !config.includeLetters && !config.includeDigits && !config.includeSymbols

  return (
    <div className="page">
      <h1>随机字符串生成器</h1>
      <p className="lead">
        根据喜好选择字符集和长度，每次生成 10 个不同的随机字符串。
      </p>
      <div className="card controls-card" aria-live="polite">
        <div className="controls">
          <label>
            <span>字符串长度（4 - 32）</span>
            <input
              type="number"
              min="4"
              max="32"
              value={config.length}
              onChange={handleLengthChange}
            />
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={config.includeLetters}
              onChange={handleToggle('includeLetters')}
            />
            字母（A-Z, a-z）
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={config.includeDigits}
              onChange={handleToggle('includeDigits')}
            />
            数字（0-9）
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={config.includeSymbols}
              onChange={handleToggle('includeSymbols')}
            />
            特殊符号（!@#...）
          </label>
        </div>
        <button
          type="button"
          className="primary-button"
          onClick={handleGenerate}
          disabled={disabled}
        >
          重新生成 10 个
        </button>
        {disabled ? (
          <p className="error">请选择至少一种字符类型。</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : null}
      </div>
      <div className="card results-card">
        <ul className="results">
          {results.map((item, index) => (
            <li className="result-item" key={`${item}-${index}`}>
              <span className="result-text">{item}</span>
              <button
                type="button"
                className={`copy-button${
                  copiedIndex === index ? ' copy-button--active' : ''
                }`}
                onClick={() => handleCopy(item, index)}
              >
                {copiedIndex === index ? '已复制' : '复制'}
              </button>
            </li>
          ))}
        </ul>
        {copyError ? <p className="error copy-error">{copyError}</p> : null}
      </div>
    </div>
  )
}

export default App
